import { useState } from 'react';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { MarketingSegment, useDeleteMarketingSegment, useMarketingSegments, usePreviewMarketingSegment } from '../../queries/marketingSegments';
import { useSubscriptionPackages } from '../../queries/subscriptionPackages';
import { SegmentFormDialog } from './SegmentFormDialog';
import { SegmentPreviewDialog } from './SegmentPreviewDialog';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';

export function SegmentsPage() {
  const { data: segments = [], isLoading } = useMarketingSegments();
  const { data: packages = [] } = useSubscriptionPackages();
  const remove = useDeleteMarketingSegment();
  const preview = usePreviewMarketingSegment();
  const { success, error } = useToast();

  const [editingSegment, setEditingSegment] = useState<MarketingSegment | null | 'new'>(null);
  const [previewingSegment, setPreviewingSegment] = useState<MarketingSegment | null>(null);
  const [deleting, setDeleting] = useState<MarketingSegment | null>(null);

  const confirmDelete = () => {
    if (!deleting) return;
    remove.mutate(deleting.id, {
      onSuccess: () => {
        success(`Deleted "${deleting.name}".`);
        setDeleting(null);
      },
      onError: (err) => {
        error(extractErrorMessage(err, 'Delete failed.'));
        setDeleting(null);
      },
    });
  };

  const handlePreview = (segment: MarketingSegment) => {
    setPreviewingSegment(segment);
    preview.mutate(segment.id, { onError: (err) => error(extractErrorMessage(err, 'Preview failed.')) });
  };

  const columns: DataTableColumn<MarketingSegment>[] = [
    { key: 'name', header: 'Name', priority: true, render: (s) => <span className="font-medium">{s.name}</span> },
    { key: 'est', header: 'Est. users', priority: true, alignEnd: true, render: (s) => <span className="tabular-nums text-muted-foreground @2xl:text-foreground">{s.estimated_count ?? '—'}</span> },
    { key: 'manual', header: 'Manually added', priority: false, alignEnd: true, render: (s) => <span className="tabular-nums text-muted-foreground @2xl:text-foreground">{s.manual_user_ids.length}</span> },
    { key: 'campaigns', header: 'Campaigns', priority: false, alignEnd: true, render: (s) => <span className="tabular-nums text-muted-foreground @2xl:text-foreground">{s._count.campaigns}</span> },
    { key: 'lastrun', header: 'Last run', priority: false, render: (s) => <span className="text-muted-foreground">{s.last_run_at ? new Date(s.last_run_at).toLocaleString() : 'Never'}</span> },
    {
      key: 'actions',
      header: '',
      priority: true,
      alignEnd: true,
      render: (s) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => handlePreview(s)} title="Preview"><Eye className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={() => setEditingSegment(s)} title="Edit"><Pencil className="h-4 w-4" /></Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setDeleting(s)}
            disabled={s._count.campaigns > 0}
            title={s._count.campaigns > 0 ? 'Cannot delete — used by a campaign' : 'Delete'}
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Saved user filters campaigns target.</p>
        <Button onClick={() => setEditingSegment('new')}>
          <Plus className="h-4 w-4" />
          Create segment
        </Button>
      </div>

      {isLoading ? <Spinner /> : <DataTable columns={columns} data={segments} getRowKey={(s) => String(s.id)} emptyMessage="No segments yet." />}

      {editingSegment && (
        <SegmentFormDialog segment={editingSegment === 'new' ? null : editingSegment} packages={packages} onClose={() => setEditingSegment(null)} />
      )}
      {previewingSegment && (
        <SegmentPreviewDialog
          segmentName={previewingSegment.name}
          isLoading={preview.isPending}
          result={preview.data ?? null}
          onClose={() => setPreviewingSegment(null)}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete segment?"
        description={`"${deleting?.name}" will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
